import React, { useEffect, useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../../../../../../../Stylesheets/Dashboard/Tabs/Labor/MarkAttendanceModalBulk.css';

import { useDispatch, useSelector } from 'react-redux';
import {
  fetchAttendanceByDate,
  fetchWorkers,
  markBulkAttendance,
  refreshLaborProject,
} from '../../../../../../../Redux/Projects/Labor/laborSlice';
import { showFeedback } from '../../../../../../../Redux/UI/uiFeedbackSlice';

const MarkAttendanceModalBulk = ({ projectId, onClose }) => {
  const dispatch = useDispatch();

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dirty, setDirty] = useState(false);
  const [attendance, setAttendance] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const { workers = [], attendanceByDate = {} } = useSelector((state) => state.labor || {});
  const isMobile = useMemo(() => window.innerWidth <= 768, []);

  const projectWorkers = useMemo(() => {
    return workers.filter((w) => String(w.projectId) === String(projectId));
  }, [workers, projectId]);

  const formattedDate = useMemo(() => {
    return selectedDate?.toISOString?.().split('T')[0];
  }, [selectedDate]);

  // ✅ Fetch workers on modal mount if empty
  useEffect(() => {
    if (projectId && projectWorkers.length === 0) {
      dispatch(fetchWorkers(projectId));
    }
  }, [projectId, projectWorkers.length, dispatch]);

  // 📅 Fetch attendance for selected date
  useEffect(() => {
    if (formattedDate && projectId) {
      dispatch(fetchAttendanceByDate({ projectId, date: formattedDate }));
    }
  }, [formattedDate, projectId, dispatch]);

  // 🔁 Sync workers with fetched attendance (only when not dirty)
  useEffect(() => {
    if (dirty) return;

    setAttendance(
      projectWorkers.map((w) => ({
        ...w,
        status: attendanceByDate?.[w._id] || null,
        date: formattedDate,
      }))
    );
  }, [attendanceByDate, projectWorkers, formattedDate, dirty]);

  // reset dirty when date changes
  useEffect(() => {
    setDirty(false);
  }, [formattedDate]);

  const setStatus = (workerId, status) => {
    setDirty(true);
    setAttendance((prev) =>
      prev.map((w) => (String(w._id) === String(workerId) ? { ...w, status } : w))
    );
  };

  const markAll = (status) => {
    setDirty(true);
    setAttendance((prev) => prev.map((w) => ({ ...w, status })));
  };

  const handleSubmit = async () => {
    const records = attendance
      .filter((w) => !!w.status)
      .map((w) => ({
        workerId: w._id,
        date: w.date,
        status: w.status,
        projectId,
      }));

    if (records.length === 0) {
      dispatch(showFeedback({ type: 'warning', title: 'Nothing to submit', message: 'Select attendance for at least one worker.' }));
      return;
    }

    try {
      setSubmitting(true);
      const result = await dispatch(markBulkAttendance(records));

      if (markBulkAttendance.fulfilled.match(result)) {
        dispatch(showFeedback({ type: 'success', title: 'Attendance saved', message: `${records.length} records submitted.` }));
        dispatch(refreshLaborProject(projectId));
        onClose();
      } else {
        const msg = result.payload?.message || result.error?.message || 'Failed to submit attendance.';
        dispatch(showFeedback({ type: 'error', title: 'Failed', message: msg }));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const loadingWorkers = projectId && projectWorkers.length === 0;

  return (
    <div className="mark-attendance-bulk-modal-overlay" onMouseDown={onClose}>
      <div className="mark-attendance-bulk-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="mark-attendance-bulk-modal-header">
          <div>
            <h2>Mark Attendance</h2>
            <p className="sub">Choose date and mark status for workers.</p>
          </div>

          <button className="close-btn" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="mark-attendance-bulk-toolbar">
          <div className="date-box">
            <span className="label">Date</span>
            <DatePicker
              selected={selectedDate}
              onChange={(d) => setSelectedDate(d || new Date())}
              className="mark-attendance-bulk-datepicker-input"
              dateFormat="dd MMM yyyy"
              disabled={submitting}
            />
          </div>

          <div className="bulk-actions">
            <button className="pill present" onClick={() => markAll('present')} disabled={submitting || loadingWorkers}>
              Mark all present
            </button>
            <button className="pill absent" onClick={() => markAll('absent')} disabled={submitting || loadingWorkers}>
              Mark all absent
            </button>
            <button className="pill leave" onClick={() => markAll('leave')} disabled={submitting || loadingWorkers}>
              Set all on leave
            </button>
          </div>
        </div>

        {loadingWorkers ? (
          <div className="mark-attendance-bulk-loading">
            <div className="spinner" />
            Loading workers…
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            {!isMobile ? (
              <div className="attendance-table-wrap">
                <table className="attendance-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Role</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attendance.map((w) => (
                      <tr key={w._id}>
                        <td className="name-cell">
                          <div className="name">{w.firstName} {w.lastName}</div>
                          <div className="muted">#{String(w._id).slice(-6)}</div>
                        </td>
                        <td className="role-cell">{w.role || '—'}</td>
                        <td>
                          <div className="segmented">
                            <button
                              type="button"
                              className={`seg present ${w.status === 'present' ? 'active' : ''}`}
                              onClick={() => setStatus(w._id, 'present')}
                              disabled={submitting}
                            >
                              Present
                            </button>
                            <button
                              type="button"
                              className={`seg absent ${w.status === 'absent' ? 'active' : ''}`}
                              onClick={() => setStatus(w._id, 'absent')}
                              disabled={submitting}
                            >
                              Absent
                            </button>
                            <button
                              type="button"
                              className={`seg leave ${w.status === 'leave' ? 'active' : ''}`}
                              onClick={() => setStatus(w._id, 'leave')}
                              disabled={submitting}
                            >
                              Leave
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              /* Mobile Cards */
              <div className="attendance-cards">
                {attendance.map((w) => (
                  <div className="attendance-card" key={w._id}>
                    <div className="card-top">
                      <div>
                        <div className="name">{w.firstName} {w.lastName}</div>
                        <div className="muted">{w.role || '—'}</div>
                      </div>
                      <div className={`status-badge ${w.status || 'none'}`}>
                        {w.status || 'not set'}
                      </div>
                    </div>

                    <div className="segmented mobile">
                      <button
                        type="button"
                        className={`seg present ${w.status === 'present' ? 'active' : ''}`}
                        onClick={() => setStatus(w._id, 'present')}
                        disabled={submitting}
                      >
                        Present
                      </button>
                      <button
                        type="button"
                        className={`seg absent ${w.status === 'absent' ? 'active' : ''}`}
                        onClick={() => setStatus(w._id, 'absent')}
                        disabled={submitting}
                      >
                        Absent
                      </button>
                      <button
                        type="button"
                        className={`seg leave ${w.status === 'leave' ? 'active' : ''}`}
                        onClick={() => setStatus(w._id, 'leave')}
                        disabled={submitting}
                      >
                        Leave
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mark-attendance-bulk-modal-footer">
              <button className="btn-primary" onClick={handleSubmit} disabled={submitting}>
                {submitting ? 'Submitting…' : 'Submit Attendance'}
              </button>
              <button className="btn-ghost" onClick={onClose} disabled={submitting}>
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MarkAttendanceModalBulk;
